const USER_ID_HEADER = 'X-User-Id'
// 历史版本会把「最近一次解析到的 userId」写进这个 key 做兜底（含历史默认用户 990）。
// 现已废弃：星盘页的 userId 只允许来自域名参数或全局登录态，残留旧值会在解析时清理。
const USER_ID_STORAGE_KEY = 'astrolabe.userId'
// 天星网站登录态的全局缓存 key：值为用户对象，用户主键是 id（不是 userId）
const TIANXING_USER_STORAGE_KEY = 'user'

let currentUserId = null
let windowLikeOverride = null
let legacyStoredUserIdPurged = false
// 登录态订阅者：localStorage.user 被写入后逐个通知。
// 用途：星盘页等消费方在「支付开通 / 刷新用户信息」后能就地重读权限，不需要整页刷新。
const tianxingUserSubscribers = new Set()

function getWindowLike () {
  if (windowLikeOverride !== null) {
    return windowLikeOverride
  }
  if (typeof window !== 'undefined') {
    return window
  }
  return null
}

function normalizeUserId (rawUserId) {
  const normalized = String(rawUserId || '').trim()
  return normalized || null
}

function removeStoredUserId () {
  const windowLike = getWindowLike()
  if (!windowLike || !windowLike.localStorage) {
    return
  }

  try {
    windowLike.localStorage.removeItem(USER_ID_STORAGE_KEY)
  } catch (error) {
    // 本地存储不可用时静默降级，不阻塞主流程。
  }
}

// 清理历史遗留的 astrolabe.userId 缓存。
// 该 key 已不再是 userId 的取值来源（星盘页只认域名参数与全局登录态），
// 但残留在浏览器里的旧值（历史默认用户 990、上一次的 URL 参数等）不能留着误导后续排查，
// 因此本次会话首次解析 userId 时统一清掉；每个模块生命周期只清一次。
function purgeLegacyStoredUserId () {
  if (legacyStoredUserIdPurged) {
    return
  }
  legacyStoredUserIdPurged = true
  removeStoredUserId()
}

function resolveUserIdFromLocation () {
  const windowLike = getWindowLike()
  if (!windowLike || !windowLike.location || !windowLike.location.search) {
    return null
  }

  try {
    const searchParams = new URLSearchParams(windowLike.location.search)
    return normalizeUserId(searchParams.get('userId'))
  } catch (error) {
    return null
  }
}

// 从全局登录态（localStorage.user）解析 userId：
// 登录态对象里字段是 id / nickName / auth_code，没有 userId 字段。
function resolveUserIdFromLoggedInUser () {
  const windowLike = getWindowLike()
  if (!windowLike || !windowLike.localStorage) {
    return null
  }

  try {
    const rawUser = windowLike.localStorage.getItem(TIANXING_USER_STORAGE_KEY)
    if (!rawUser) {
      return null
    }
    const loggedInUser = JSON.parse(rawUser)
    return normalizeUserId(loggedInUser && loggedInUser.id)
  } catch (error) {
    return null
  }
}

// 读取天星网站全局登录态对象（localStorage.user）。
// 首页 /tianxing/home 登录成功后，会把 /yljx/public/login 返回的 user 整对象写到这里，
// 因此星盘页可以从这里拿到会员的各项权益到期时间。
function readStoredTianxingUser () {
  const windowLike = getWindowLike()
  if (!windowLike || !windowLike.localStorage) {
    return null
  }

  try {
    const rawUser = windowLike.localStorage.getItem(TIANXING_USER_STORAGE_KEY)
    if (!rawUser) {
      return null
    }
    const parsedUser = JSON.parse(rawUser)
    return parsedUser && typeof parsedUser === 'object' ? parsedUser : null
  } catch (error) {
    return null
  }
}

/**
 * 写入全局登录态（localStorage.user）并广播变更。
 *
 * 全站统一写入口（与 readStoredTianxingUser 成对）：登录 / 换绑 / 支付开通 /
 * 刷新用户信息都走这里；写入后订阅方（星盘页、顶栏等）可就地重读最新权限，
 * 不再依赖 window.location.reload()。内容与现有值一致时不重复写、也不广播，
 * 避免定时刷新（refreshTianxingUser）造成无意义的刷新抖动。
 *
 * @param {Object} user 后端返回的完整 user 对象
 * @returns {Object|null} 写入的 user；入参非法或本地存储不可用时返回 null
 */
function writeStoredTianxingUser (user) {
  if (!user || typeof user !== 'object') {
    return null
  }
  const windowLike = getWindowLike()
  if (!windowLike || !windowLike.localStorage) {
    return null
  }

  let serialized = ''
  try {
    serialized = JSON.stringify(user)
  } catch (error) {
    return null
  }

  try {
    if (windowLike.localStorage.getItem(TIANXING_USER_STORAGE_KEY) === serialized) {
      return user
    }
    windowLike.localStorage.setItem(TIANXING_USER_STORAGE_KEY, serialized)
  } catch (error) {
    return null
  }

  tianxingUserSubscribers.forEach(handler => {
    try {
      handler(user)
    } catch (error) {
      // 单个订阅方异常不影响其它订阅方与主流程
    }
  })
  return user
}

/**
 * 订阅全局登录态变更（每次 localStorage.user 被写入后触发）。
 * @param {Function} handler 收到最新 user 对象
 * @returns {Function} 取消订阅函数
 */
function subscribeTianxingUser (handler) {
  if (typeof handler !== 'function') {
    return () => {}
  }
  tianxingUserSubscribers.add(handler)
  return () => {
    tianxingUserSubscribers.delete(handler)
  }
}

/** 清空订阅者（仅供单测复位使用） */
function resetTianxingUserSubscribersForTest () {
  tianxingUserSubscribers.clear()
}

// 按「到期时间」判断某项权益是否仍有效。
// 口径：没有该字段（或为空）= 没有权限；有值且当前时间未超过到期时间 = 有权限。
function isEntitlementActive (expiry) {
  if (expiry === undefined || expiry === null || `${expiry}`.trim() === '') {
    return false
  }
  // 后端返回形如 "yyyy-MM-dd HH:mm:ss"，部分浏览器对 '-' 分隔解析不一致，统一换成 '/'
  const parsedTime = new Date(`${expiry}`.trim().replace(/-/g, '/')).getTime()
  if (!Number.isFinite(parsedTime)) {
    return false
  }
  return Date.now() <= parsedTime
}

// 报警相关权益：点报警（pointAlarm）/ 面报警（areaAlarm）。
// 数据来自全局登录态里的 pointAlarm / areaAlarm 到期时间
// （对应 users 表 point_alarm / area_alarm 两列，由后台「权限设置」维护）。
function getAlarmEntitlement () {
  const storedUser = readStoredTianxingUser() || {}
  return {
    pointAlarm: isEntitlementActive(storedUser.pointAlarm),
    areaAlarm: isEntitlementActive(storedUser.areaAlarm)
  }
}

function setCurrentUserId (userId) {
  // 只在内存中生效，不再写入 astrolabe.userId 缓存（该缓存已废弃）。
  currentUserId = normalizeUserId(userId)
  return currentUserId
}

function getCurrentUserId () {
  if (currentUserId) {
    return currentUserId
  }

  // 星盘页 userId 只有两种合法来源：
  //   1) 全局登录态：localStorage.user 的 id（天星首页登录成功后写入）；
  //   2) 域名参数：/onlineChart?userId=893。
  // 两者都取不到时返回 null，由 /onlineChart 提示用户先登录账号再使用。
  purgeLegacyStoredUserId()
  currentUserId = resolveUserIdFromLoggedInUser() || resolveUserIdFromLocation()
  return currentUserId
}

function buildUserContextHeaders (headers = {}) {
  const userId = getCurrentUserId()
  if (!userId) {
    return headers
  }
  return {
    ...headers,
    [USER_ID_HEADER]: userId
  }
}

function resetUserContextForTest () {
  currentUserId = null
  windowLikeOverride = null
  legacyStoredUserIdPurged = false
}

function setWindowLikeForTest (windowLike) {
  windowLikeOverride = windowLike
}

module.exports = {
  USER_ID_HEADER,
  USER_ID_STORAGE_KEY,
  TIANXING_USER_STORAGE_KEY,
  normalizeUserId,
  resolveUserIdFromLoggedInUser,
  readStoredTianxingUser,
  writeStoredTianxingUser,
  subscribeTianxingUser,
  resetTianxingUserSubscribersForTest,
  isEntitlementActive,
  getAlarmEntitlement,
  setCurrentUserId,
  getCurrentUserId,
  buildUserContextHeaders,
  resetUserContextForTest,
  setWindowLikeForTest
}
